import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { VideoPlayerProps, VideoPlayerRef } from './VideoPlayer.data';
import styles from './VideoPlayer.module.scss';
import useMount from '../../../../hooks/useMount';
import VimeoPlayer, { placeholderVimeoUrl } from './VimeoPlayer';
import useUnmount from '../../../../hooks/useUnmount';
import FilePlayer, { placeholderVideoFileUrl } from './FilePlayer';
import { getPlayerType } from '../utils';
import useUpdateEffect from '../../../../hooks/useUpdateEffect';
import type { PlayerType } from '../Video.data';

// If NO url is given (for example video-modal),
// we use a placeholder (blank video of 1s) in order to construct/load the video-player.
const getUrl = (
  url: string | undefined,
  playerType: PlayerType | undefined,
): string | undefined => {
  if (!url) {
    if (playerType === 'vimeo') {
      return placeholderVimeoUrl;
    }
    if (playerType === 'file') {
      return placeholderVideoFileUrl;
    }
  }

  return url;
};

export default function VideoPlayer({
  className,
  ref,
  ...playerProps
}: VideoPlayerProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<VideoPlayerRef | null>).current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  const playerRef = useRef<VimeoPlayer | FilePlayer>(undefined);
  const [newUrl, setNewUrl] = useState<string | undefined>(
    getUrl(playerProps?.url, playerProps?.playerType),
  );
  const [playerType, setPlayerType] = useState<PlayerType>();

  useEffect(() => {
    setNewUrl(getUrl(playerProps.url, playerProps.playerType));
  }, [playerProps.url, playerProps.playerType]);

  useEffect(() => {
    setPlayerType(getPlayerType(newUrl));
  }, [newUrl]);

  useMount(() => {
    if (elementRef.current) {
      const urlType = getPlayerType(playerProps?.url);
      if (urlType === 'vimeo' || (playerProps && playerProps.playerType === 'vimeo')) {
        playerRef.current = new VimeoPlayer(elementRef.current, { ...playerProps, url: newUrl });
      }
      if (urlType === 'file' || (playerProps && playerProps.playerType === 'file')) {
        playerRef.current = new FilePlayer(elementRef.current, { ...playerProps, url: newUrl });
      }
    }
  });

  useUpdateEffect(() => {
    // load new video
    playerRef?.current?.setVideo(newUrl);
  }, [newUrl, playerRef]);

  useUnmount(() => {
    playerRef?.current?.dispose();
  });

  useEffect(() => {
    if (typeof playerProps?.volume === 'number') {
      playerRef?.current?.setVolume(playerProps?.volume);
    }
  }, [playerProps.volume]);

  useEffect(() => {
    playerRef?.current?.setMuted(!!playerProps?.muted);
  }, [playerProps.muted]);

  const seekTo = useCallback((time: number) => {
    playerRef?.current?.seekTo(time);
  }, []);

  const play = useCallback(() => playerRef.current?.play(), []);
  const pause = useCallback(() => playerRef.current?.pause(), []);
  const getCurrentTime = useCallback(() => playerRef?.current?.getCurrentTime() || 0, []);
  const getDuration = useCallback(() => playerRef?.current?.getDuration() || 0, []);
  const getInternalPlayer = useCallback(() => playerRef?.current?.getInternalPlayer(), []);

  useImperativeHandle(
    ref,
    () => ({
      elementRef,
      getInternalPlayer,
      seekTo,
      getCurrentTime,
      getDuration,
      play,
      pause,
    }),
    [elementRef, getInternalPlayer, seekTo, getCurrentTime, getDuration, play, pause],
  );

  return (
    <div
      // id={url}
      data-vimeo-url={playerType === 'vimeo' ? newUrl : null}
      ref={elementRef}
      className={classNames(styles.videoPlayer, className)}
    ></div>
  );
}
